import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { AuthService, StateService } from '@cosmos/angular/utils';
import { TranslatePipe } from '@ngx-translate/core';

import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { Password, PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';

import { AuthLayoutComponent } from '../auth-layout/auth-layout.component';

@Component({
  selector: 'cosmos-angular-ui-login',
  imports: [
    TranslatePipe,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    FloatLabelModule,
    InputTextModule,
    PasswordModule,
    ButtonModule,
    Password,
    MessageModule,
    AuthLayoutComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit {

  protected readonly loading = signal(false);
  protected readonly error = signal<'credentials' | 'rate-limit' | 'role' | null>(null);
  protected readonly signupAllowed = signal(false);

  loginForm!: FormGroup;

  private readonly authService = inject(AuthService);
  private readonly stateService = inject(StateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);

  constructor() {
    if (this.stateService.getCurrentUser()) {
      this.router.navigate(['/']);
    }

    this.route.data.subscribe(data => {
      this.signupAllowed.set(!!data['signupAllowed']);
    });

    this.route.queryParams.subscribe(params => {
      if (params['error'] === 'role') this.error.set('role');
    });
  }

  ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }

  get f() { return this.loginForm.controls; }

  onSubmit(): void {
    if (this.loginForm.invalid) return;

    this.loading.set(true);
    this.error.set(null);

    this.authService.login(this.f['email'].value, this.f['password'].value).subscribe({
      next: (response: any) => {
        if (response) {
          const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
          this.loading.set(false);
          this.router.navigate([returnUrl]);
        } else {
          this.loading.set(false);
        }
      },
      error: (err: any) => {
        this.loading.set(false);
        this.error.set(err?.status === 429 ? 'rate-limit' : 'credentials');
      },
    });
  }
}
